/**
 * ProjectOfficialArtifactPreviewView — `/projects/$projectId/official/$artifactId/preview`
 *
 * Vista previa del draft mientras el autor edita. Wrapper sobre
 * `ArtifactPreview mode='preview'` con un banner explícito que
 * comunica que el doc aún NO es oficial, y un botón para volver al
 * editor.
 *
 * Flujo:
 *   1. Autor edita en `/official/$id/edit`.
 *   2. Editor autosalva con debounce (ya existente).
 *   3. Autor click "Vista previa" → este componente.
 *   4. Hace fetch fresh del artifact + version → render unificado vía
 *      ArtifactPreview.
 *   5. "Volver al editor" → regresa a `/official/$id/edit`.
 *
 * Mismo render que el viewer (`ProjectOfficialArtifactView`) pero sin
 * panel de metadata (versión/hash/firmantes no aplican a un draft) y
 * con el banner soft.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MessageSquare, Pencil } from 'lucide-react'

import { ApiError } from '../../api/client'
import { getArtifact, getArtifactVersion } from '../../api/artifacts'
import { useVersionCitations } from '../../api/citations'
import { listActiveComments } from '../../api/comments'
import { listProjectMembers } from '../../api/project_members'
import {
  fetchSnapshotTemplate,
  type SnapshotTemplateItem,
} from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { DisabledActionButton } from '../../components/DisabledActionButton'
import { CitationsLayer } from '../../components/editor/CitationsLayer'
import { CommentsPanel } from '../../components/projects/CommentsPanel'
import { ArtifactPreview } from '../../components/projects/official-viewer/ArtifactPreview'
import { DownloadPdfButton } from '../../components/projects/official-viewer/DownloadPdfButton'
import type {
  TemplateDefinition,
  TemplateI18nSection,
} from '../../components/projects/official-viewer/section_types'
import { RightSidePanel } from '../../components/RightSidePanel'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { useProjectAccess } from '../../hooks/useProjectAccess'
import { canEditArtifact, canEditSomeArtifact, isExternal } from '../../lib/tiers'
import { useBreadcrumbExtension, useProjectContext } from './ProjectShell'
import { useRightPanel } from './rightPanelSlot'

export function ProjectOfficialArtifactPreviewView() {
  const { t } = useTranslation(['projects', 'errors'])
  const { project } = useProjectContext()
  const { accessToken } = useAccessToken()
  const { me, isLoading: meIsLoading } = useMe()
  const navigate = useNavigate()
  const { artifactId } = useParams({
    from: '/projects/$projectId/official/$artifactId/preview',
  })
  // Doc 21 Fase 5/6 — toggle del RightSidePanel para acceder a los
  // comentarios desde el preview. El autor sigue siendo el actor
  // ejerciendo cambios; al previsualizar puede querer ver/responder
  // los comentarios pendientes sin volver al editor.
  const [panelOpen, setPanelOpen] = useState(false)

  const artifactQuery = useQuery({
    queryKey: ['artifacts', artifactId],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getArtifact(accessToken, artifactId)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  // Solo EL template de este artefacto (no el snapshot completo ~700KB). El
  // snapshot es inmutable → `staleTime: Infinity` (un fetch por sesión).
  const templateKey = artifactQuery.data?.template_key ?? null
  const templateQuery = useQuery({
    queryKey: ['projects', project.id, 'snapshot', 'template', templateKey],
    queryFn: () => {
      if (!accessToken || !templateKey)
        throw new Error('no_access_token_or_template_key')
      return fetchSnapshotTemplate(accessToken, project.id, templateKey)
    },
    enabled: !!accessToken && !!templateKey,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
    retry: false,
  })

  const currentVersionId = artifactQuery.data?.current_version_id ?? null
  const versionQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', currentVersionId],
    queryFn: () => {
      if (!accessToken || !currentVersionId)
        throw new Error('no_access_token_or_version')
      return getArtifactVersion(accessToken, artifactId, currentVersionId)
    },
    enabled: !!accessToken && !!currentVersionId,
    staleTime: 60_000,
    retry: false,
  })

  // OT-0009 N3 — "Volver al editor" no verificaba `canEditArtifact`
  // antes de ofrecerse. El destino ya tiene el gate de página (Fase 4),
  // así que no había pérdida de trabajo real, pero igual se ofrecía un
  // control que el criterio §4.1 pide ocultar/deshabilitar.
  //
  // Deshabilitada para `external` — mismo motivo que en el viewer
  // (`ProjectOfficialArtifactView.tsx`): la query siempre falla para ese
  // tier y bloqueaba la página entera sin necesidad, ya que
  // `canEditArtifact` lo excluye antes de mirar `isMember`/`isLead`.
  // `!meIsLoading` evita la carrera con `useMe()` — ver nota en el viewer.
  const membersQuery = useQuery({
    queryKey: ['projects', project.id, 'members'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, project.id)
    },
    enabled: !!accessToken && !meIsLoading && !isExternal(me?.role),
    staleTime: 30_000,
    retry: false,
  })
  const { isMember, isLead, section } = useProjectAccess(project, membersQuery.data ?? [])

  // ADR-0001 Tier 1 — citas IA persistidas por versión. CitationsLayer
  // las consume para mostrar el popover al click en `[N]`. En el
  // preview el lector también necesita ver de dónde viene cada cita.
  const citationsQuery = useVersionCitations({
    artifactId,
    versionId: currentVersionId,
  })

  // Doc 21 Fase 5.d — count de comments activos para el badge del
  // botón Comentarios. Mismo query key que el editor para reusar cache.
  const activeCommentsQuery = useQuery({
    queryKey: [
      'artifacts',
      artifactId,
      'active-comments',
      currentVersionId,
    ],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listActiveComments(accessToken, artifactId, {
        versionId: currentVersionId,
      })
    },
    enabled: !!accessToken,
    staleTime: 15_000,
    select: (rows) => rows.filter((r) => r.kind === 'comment').length,
  })
  const activeCommentsCount = activeCommentsQuery.data ?? 0

  const snapshotTemplate: SnapshotTemplateItem | null =
    templateQuery.data ?? null

  const templateDef: TemplateDefinition | null = useMemo(() => {
    if (!snapshotTemplate) return null
    return snapshotTemplate.definition as unknown as TemplateDefinition
  }, [snapshotTemplate])

  const artifactNameForBreadcrumb =
    artifactQuery.data?.name ?? artifactQuery.data?.template_key ?? '…'
  useBreadcrumbExtension([
    { label: t('shell.tabs.lifecycle') },
    { label: artifactNameForBreadcrumb },
    { label: t('official_preview.breadcrumb') },
  ])

  const renderLocale = useMemo<'es' | 'en'>(() => {
    if (!templateDef?.i18n) return 'es'
    const projectLang = project.content_language
    if (
      (projectLang === 'es' || projectLang === 'en') &&
      templateDef.i18n[projectLang]
    ) {
      return projectLang
    }
    if (templateDef.i18n.es) return 'es'
    if (templateDef.i18n.en) return 'en'
    return 'es'
  }, [templateDef, project.content_language])

  // ─── Render gates ────────────────────────────────────────────────────
  if (
    meIsLoading ||
    artifactQuery.isLoading ||
    templateQuery.isLoading ||
    membersQuery.isLoading ||
    (currentVersionId && versionQuery.isLoading)
  ) {
    return (
      <ScrollPage padding="tight">
        <p className="font-mono text-xs text-ink-subtle">
          {t('official_viewer.loading')}
        </p>
      </ScrollPage>
    )
  }
  if (templateQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={templateQuery.error} />
      </ScrollPage>
    )
  }
  if (artifactQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={artifactQuery.error} />
      </ScrollPage>
    )
  }
  // `membersQuery` NO corta el render. Solo alimenta `isMember`/`isLead`,
  // que deciden si una acción se ofrece activa o deshabilitada — es una
  // pista de permisos, no el contenido de la página. Tumbar la lectura
  // del documento oficial porque falló listar miembros invierte la
  // mitigación de la propia OT-0009 (§8: "ante la duda, habilitar") y
  // convierte un fallo transitorio en una página inservible. Si falla,
  // `membersQuery.data ?? []` deja `isMember`/`isLead` en `false`: se
  // ofrece de menos, que es el lado seguro, y el backend sigue siendo
  // quien enforce.

  const artifact = artifactQuery.data
  if (!artifact) return null

  const editingTierAtAll = canEditSomeArtifact(me?.role, me?.tenant?.access)
  const canEditThisArtifact = canEditArtifact(me?.role, me?.tenant?.access, {
    isMember,
    isLead,
    section,
    isOwner: artifact.owner_user_id === me?.id,
  })

  if (!currentVersionId) {
    return (
      <ScrollPage padding="tight">
        <div className="max-w-5xl">
          <div className="rounded-md border border-review/40 bg-review-soft/40 p-4 font-body text-sm text-ink-muted">
            {t('official_viewer.errors.no_current_version')}
          </div>
        </div>
      </ScrollPage>
    )
  }

  if (versionQuery.error) {
    return (
      <ScrollPage padding="tight">
        <ErrorBox error={versionQuery.error} />
      </ScrollPage>
    )
  }
  const version = versionQuery.data
  if (!version) return null
  // OT-0064 — permiso y estado se combinan: con la versión bloqueada
  // (p.ej. «Atrás» hasta aquí con el documento ya en revisión) no se
  // ofrece el editor ni «Subsanar con IA», que acabaría rechazado al aplicar.
  const canEditThisVersion = canEditThisArtifact && version.is_editable

  if (!snapshotTemplate || !templateDef) {
    return (
      <ScrollPage padding="tight">
        <div className="max-w-5xl">
          <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-body text-sm text-ink-muted">
            {t('official_viewer.errors.template_not_in_snapshot', {
              template_key: artifact.template_key,
            })}
          </div>
        </div>
      </ScrollPage>
    )
  }

  const backLink = {
    label: t('official_preview.back_to_editor'),
    fallback: () =>
      void navigate({
        to: '/projects/$projectId/official/$artifactId/edit',
        params: { projectId: project.id, artifactId: artifact.id },
        search: { v: undefined },
      }),
  }

  return (
    // Layout flex column: toolbar arriba (shrink-0) + contenido
    // scrolleable abajo (flex-1). Mismo patrón que viewer y editor —
    // "Volver al editor" y "Comentarios" siempre visibles, sin importar
    // dónde scrollea el lector.
    <div className="h-full flex flex-col min-h-0">
      {/* Wrapper `max-w-5xl` alinea los botones al ras del borde derecho
          del documento (misma columna de contenido), no al borde de la
          pantalla. */}
      <div className="shrink-0 bg-surface border-b border-line px-8 py-2.5">
        <div className="max-w-5xl flex items-center justify-end gap-2">
          {editingTierAtAll && version.is_editable ? (
            canEditThisArtifact ? (
              <Link
                to="/projects/$projectId/official/$artifactId/edit"
                params={{ projectId: project.id, artifactId: artifact.id }}
                search={{ v: undefined }}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand text-canvas px-3 py-1.5 text-xs font-medium hover:bg-brand-deep transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                {t('official_preview.back_to_editor')}
              </Link>
            ) : (
              <DisabledActionButton
                title={t('lifecycle.drill_in.row.edit_disabled_tooltip')}
                className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink-subtle cursor-not-allowed"
              >
                <Pencil className="w-3.5 h-3.5" />
                {t('official_preview.back_to_editor')}
              </DisabledActionButton>
            )
          ) : null}
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className={
              panelOpen
                ? 'inline-flex items-center gap-1.5 rounded-md border border-brand bg-brand-soft px-3 py-1.5 text-xs font-medium text-brand transition-colors'
                : 'inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink hover:border-line-strong hover:bg-surface-muted transition-colors'
            }
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {t('comments_panel.tab_label')}
            {activeCommentsCount > 0 ? (
              <span className="px-1.5 py-0.5 rounded-full bg-review-soft text-review text-[10px] font-mono">
                {activeCommentsCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-8 py-8">
        <CitationsLayer citations={citationsQuery.data ?? []}>
        <ArtifactPreview
          artifact={artifact}
          version={version}
          templateDef={templateDef}
          renderLocale={renderLocale}
          mode="preview"
          // Con la versión bloqueada no se ofrece volver al editor: el
          // historial llevaría a su vista de bloqueo (OT-0064).
          backLink={version.is_editable ? backLink : null}
          topToolbar={<DownloadPdfButton artifact={artifact} version={version} />}
          banner={
            <div className="flex items-start gap-3 rounded-md border border-review/30 bg-review-soft/40 px-4 py-3 text-xs text-ink">
              <div className="flex-1">
                <p className="font-medium uppercase tracking-wider text-[10px] text-review mb-0.5">
                  {t('official_preview.banner_eyebrow')}
                </p>
                <p className="font-body leading-relaxed">
                  {t('official_preview.banner_body')}
                </p>
              </div>
            </div>
          }
          // TODO(B.6.c.2): cuando llegue review flow, agregar acción
          // "Enviar a revisión" como footerActions desde aquí (después de
          // que el editor confirme que no hay cambios sin guardar).
        />
        </CitationsLayer>
      </div>

      <PreviewCommentsPanelSlot
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        artifactId={artifact.id}
        versionId={currentVersionId}
        artifactAuthorId={artifact.created_by_user_id}
        isLead={isLead}
        canUseAiSuggestions={canEditThisVersion}
        projectId={project.id}
        currentUserId={me?.id ?? null}
        i18nSectionsByCode={buildI18nMap(templateDef, renderLocale)}
        activeCommentsCount={activeCommentsCount}
        versionContent={
          (version.content ?? null) as Record<string, unknown> | null
        }
      />
    </div>
  )
}

// ─── Comments panel slot (headless) ───────────────────────────────
// Registra el RightSidePanel del preview en el slot del ProjectShell.
// Sub-componente para que `useRightPanel` se llame siempre en el
// mismo punto del árbol cuando el padre ya pasó los gates de carga.
interface PreviewCommentsPanelSlotProps {
  open: boolean
  onClose: () => void
  artifactId: string
  versionId: string | null
  artifactAuthorId: string | null
  /** OT-0009 — espejo de `_is_transition_allowed` para "Reponer"
   *  comentario: exige líder del proyecto, sin excepción de admin. */
  isLead: boolean
  /** OT-0016 — mismo `canEditThisArtifact` que ya calcula el padre;
   *  gatea la asistencia de IA del panel (generar, aplicar, descartar). */
  canUseAiSuggestions: boolean
  projectId: string
  currentUserId: string | null
  i18nSectionsByCode: Map<string, TemplateI18nSection>
  activeCommentsCount: number
  /** Doc 21 Fase 10 — content vivo de la versión para que el panel
   *  resuelva el valor de cada sección anclada a un comment y muestre
   *  el diff snapshot vs vivo en el `CommentDiffBlock`. */
  versionContent: Record<string, unknown> | null
}

function PreviewCommentsPanelSlot({
  open,
  onClose,
  artifactId,
  versionId,
  artifactAuthorId,
  isLead,
  canUseAiSuggestions,
  projectId,
  currentUserId,
  i18nSectionsByCode,
  activeCommentsCount,
  versionContent,
}: PreviewCommentsPanelSlotProps) {
  const { t } = useTranslation('projects')
  return useRightPanel(
    open ? (
      <RightSidePanel
        onClose={onClose}
        ariaLabel={t('comments_panel.tab_label')}
        tabs={[
          {
            key: 'comments',
            label: t('comments_panel.tab_label'),
            icon: <MessageSquare className="w-3.5 h-3.5" />,
            count: activeCommentsCount,
            content: (
              <CommentsPanel
                artifactId={artifactId}
                versionId={versionId}
                currentUserId={currentUserId}
                artifactAuthorId={artifactAuthorId}
                isLead={isLead}
                canUseAiSuggestions={canUseAiSuggestions}
                projectId={projectId}
                templateI18nMap={i18nSectionsByCode}
                versionContent={versionContent}
              />
            ),
          },
        ]}
      />
    ) : null,
  )
}

/** Construye el Map section_id → i18n para el render del panel.
 *  Igual al patrón del viewer/editor — extracted aquí porque el preview
 *  no tenía esta dependencia antes. */
function buildI18nMap(
  templateDef: TemplateDefinition,
  locale: 'es' | 'en',
): Map<string, TemplateI18nSection> {
  const m = new Map<string, TemplateI18nSection>()
  const i18nBlock = templateDef.i18n?.[locale]
  if (i18nBlock?.sections) {
    for (const s of i18nBlock.sections) m.set(s.section_id, s)
  }
  return m
}

// ─── ErrorBox ───────────────────────────────────────────────────────────────
function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
